/**
 * Finanzen-Tab helpers — reine Formatierungs-/Build-Logik für den
 * Finanzen-Tab (Tab 2) im Fahrzeug-Detail-Drawer.
 *
 * Alle Funktionen sind pure und seiteneffektfrei, damit sie in Unit-Tests
 * ohne React- oder DB-Setup geprüft werden können. Die Tab-Komponente
 * selbst rendert nur die von `buildFinanzenRows` zurückgegebenen Zeilen.
 */

export interface FahrzeugFinanzen {
  kaufpreis_netto: number | null;
  kaufpreis_brutto: number | null;
  bruttolistenpreis: number | null;
  anschaffungsdatum: string | null;
  finanzierungspartner: string | null;
  darlehensnummer: string | null;
  monatliche_rate: number | null;
  restschuld: number | null;
  laufzeitende: string | null;
  afa_beginn: string | null;
  afa_nutzungsdauer_jahre: number | null;
  restbuchwert: number | null;
  buchhaltungskonto: string | null;
}

export interface FinanzenRow {
  label: string;
  value: string;
}

const EM_DASH = '—';

export function formatEuro(value: number | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

export function formatDateDE(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function formatNutzungsdauer(jahre: number): string {
  return jahre === 1 ? '1 Jahr' : `${jahre} Jahre`;
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

/**
 * Baut die anzuzeigenden Zeilen in der in #186 festgelegten Reihenfolge.
 * Felder mit `null`/`undefined`/leerem String werden übersprungen. `0` ist
 * ein legitimer Wert (z.B. Restschuld = 0 bei ausfinanziertem Fahrzeug) und
 * wird daher NICHT ausgefiltert.
 */
export function buildFinanzenRows(fz: FahrzeugFinanzen): FinanzenRow[] {
  const rows: FinanzenRow[] = [];
  if (hasNumber(fz.kaufpreis_netto)) {
    rows.push({ label: 'Kaufpreis netto', value: formatEuro(fz.kaufpreis_netto) });
  }
  if (hasNumber(fz.kaufpreis_brutto)) {
    rows.push({ label: 'Kaufpreis brutto', value: formatEuro(fz.kaufpreis_brutto) });
  }
  if (hasNumber(fz.bruttolistenpreis)) {
    rows.push({ label: 'Bruttolistenpreis', value: formatEuro(fz.bruttolistenpreis) });
  }
  if (hasText(fz.anschaffungsdatum)) {
    rows.push({ label: 'Anschaffungsdatum', value: formatDateDE(fz.anschaffungsdatum) });
  }
  if (hasText(fz.finanzierungspartner)) {
    rows.push({ label: 'Finanzierungspartner', value: fz.finanzierungspartner.trim() });
  }
  if (hasText(fz.darlehensnummer)) {
    rows.push({ label: 'Darlehensnummer', value: fz.darlehensnummer.trim() });
  }
  if (hasNumber(fz.monatliche_rate)) {
    rows.push({ label: 'Monatliche Rate', value: formatEuro(fz.monatliche_rate) });
  }
  if (hasNumber(fz.restschuld)) {
    rows.push({ label: 'Restschuld', value: formatEuro(fz.restschuld) });
  }
  if (hasText(fz.laufzeitende)) {
    rows.push({ label: 'Laufzeitende', value: formatDateDE(fz.laufzeitende) });
  }
  if (hasText(fz.afa_beginn)) {
    rows.push({ label: 'AfA-Beginn', value: formatDateDE(fz.afa_beginn) });
  }
  if (hasNumber(fz.afa_nutzungsdauer_jahre)) {
    rows.push({ label: 'Nutzungsdauer', value: formatNutzungsdauer(fz.afa_nutzungsdauer_jahre) });
  }
  if (hasNumber(fz.restbuchwert)) {
    rows.push({ label: 'Restbuchwert', value: formatEuro(fz.restbuchwert) });
  }
  if (hasText(fz.buchhaltungskonto)) {
    rows.push({ label: 'Buchhaltungskonto', value: fz.buchhaltungskonto.trim() });
  }
  return rows;
}
